import { useLocation } from 'react-router-dom';
import { CarCard } from '../../../components/car/searchResult/carCard';
import { useQuery } from '@apollo/client';
import {
  FIND_AVAILABLE_CARS_WITH_DELIVERY,
  GET_CAR_MAKES_AND_MODELS,
} from '../../../services/carService/queries';
import { getCurrentSiteHost } from '../../../utils/siteHost';
import queryString from 'query-string';
import tw from 'twin.macro';
import styled from 'styled-components';
import { Colors } from '../../../styles/colors';
import { combinePickerDateTimeGetUTC } from '../../../helpers/combinePickerDateTimeGetUTC';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faClock,
  faExclamationCircle,
  faCalendarAlt,
  faMapMarkerAlt,
  faExclamationTriangle,
  faMap,
  faList,
  faChevronUp,
  faGasPump,
  faLeaf,
  faBolt,
  faOilCan,
  faCircleCheck,
  faArrowsRotate,
  faBicycle,
  faCamera,
  faCarSide,
  faChild,
  faFire,
  faKey,
  faLocationCrosshairs,
  faMusic,
  faPaw,
  faRoad,
  faSnowflake,
  faSun,
  faWheelchair,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import { faAndroid, faApple, faBluetooth } from '@fortawesome/free-brands-svg-icons';
import { faUsb } from '@fortawesome/free-brands-svg-icons';
import VehicleIcon from '../../../components/vehicleIcons';
import { ErrorCode, ICalculatedPriceInfo, ProductType } from '../../../../__generated__/graphql';
import { getSiteConfig } from '../../../constants/siteConfig';
import { Link } from 'react-router-dom';
import { useGetLocationLazyQuery } from '../../../services/locationServices/__generated__/queries.generated';
import { SearchResultsSkeleton } from '../../../components/skeletons';
import { BookCard } from '../../../components/bookCard';
import { parseTripModeParams, shiftPickerDate } from '../../../helpers/monthlyTrip';
import { SimilarDatesSection } from './SimilarDatesSection';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Map, Car } from '../../../components/map';
import { useFavorites } from '../../../hooks/useFavorites';
import { authStore } from '../../../../store/authStore';
import { formatAddress } from '../../../helpers/formatAddress';
import { Modal } from '../../../components/modal';
import {
  FEATURE_OPTIONS,
  FUEL_TYPE_OPTIONS,
  SEAT_OPTIONS,
  SORT_OPTIONS,
  TRANSMISSION_OPTIONS,
  VEHICLE_TYPE_OPTIONS,
} from '../../../constants/car-filters';
import { NAV_BAR_HEIGHT_PX } from '../../../components/navBar/constants';
import { SearchNavSummary } from '../../../components/navBar/SearchNavSummary';
import { useSearchNavSlot } from '../../../components/navBar/searchNavSlot';
import { SCREENS, SCREENS_MAX, SCREENS_PX } from '../../../components/responsive';

export interface ISearchResultPageProps {
  selectedPickupLocation: String;
  selectedReturnLocation: String;
  startDate: Date;
  returnDate: Date;
  startTime: string;
  returnTime: string;
  timezone: string;
  driverAge: number;
}

type AirportSearchTab = 'airport' | 'offAirport';

type AirportContext = {
  id: number;
  name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  timezone?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

type AirportSearchCar = {
  id: string;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  weekdayPrice?: number | null;
  images?: { imageUrl?: string | null }[] | null;
  location?: {
    id?: number | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    timezone?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    taxRate?: number | null;
  } | null;
  latitude?: number | null;
  longitude?: number | null;
  calculatedPrice?: ICalculatedPriceInfo | null;
  distanceMiles?: number | null;
  isFavorite?: boolean | null;
};

type AvailableCarsWithDeliveryResponse = {
  availableCarsWithDelivery: {
    isAirportSearch: boolean;
    airportContext?: AirportContext | null;
    atAirportCars: AirportSearchCar[];
    offAirportCars: AirportSearchCar[];
  };
};

type CarSearchFilters = {
  sortBy?: string;
  minPrice?: number;
  maxPrice?: number;
  vehicleTypes?: string[];
  fuelTypes?: string[];
  features?: string[];
  minMileageLimit?: number;
  unlimitedMileage?: boolean;
  makeModels?: { make: string; model: string }[];
  minYear?: number;
  maxYear?: number;
  minSeats?: number;
  transmission?: string;
  isAddressSearch?: boolean;
};

const searchPageHeight = (viewportUnit: 'svh' | 'dvh' | 'vh') =>
  `calc(100${viewportUnit} - ${NAV_BAR_HEIGHT_PX}px - var(--staging-banner-height, 0px))`;

const PageContainer = styled.div`
  ${tw`
    box-border
    w-full
    flex
    flex-row
    relative
    overflow-hidden max-w-full min-w-0`}
  /* Fill the remaining viewport under navbar + staging banner (not 100dvh, which clips the map card). */
  height: ${searchPageHeight('svh')};
  @supports not (height: 100svh) {
    height: ${searchPageHeight('dvh')};
  }
  @supports not (height: 100dvh) {
    height: ${searchPageHeight('vh')};
  }
  /* Safe area padding for landscape mode */
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
`;

const LeftPanel = styled.div<{ $show?: boolean; $isSearchExpanded?: boolean }>`
  ${tw`
    flex
    flex-col
    relative
    z-0
    max-w-[680px]
    w-full
  `}
  background-color: ${Colors.BgSecondary};
  overflow-y: ${props => (props.$isSearchExpanded ? 'hidden' : 'auto')};
  /* On medium+ screens: always visible, fixed width */
  @media (min-width: ${SCREENS.md}) {
    width: 680px;
    min-width: 680px;
  }
  /* On smaller screens: hidden by default, shown when viewMode === 'list' */
  @media (max-width: 767px) {
    display: ${props => (props.$show ? 'flex' : 'none')};
  }
`;

const RightPanel = styled.div<{ $show?: boolean }>`
  ${tw`
    relative
    bg-white
    z-0
    flex-1
    min-w-0
    min-h-0
  `}
  height: 100%;
  /* On medium+ screens: always visible */
  @media (min-width: ${SCREENS.md}) {
    display: block;
  }
  /* On smaller screens: shown when viewMode === 'map' */
  @media (max-width: ${SCREENS_MAX.md}) {
    display: ${props => (props.$show ? 'block' : 'none')};
  }
`;

const SearchBarContainer = styled.div`
  position: fixed;
  top: calc(${NAV_BAR_HEIGHT_PX}px + var(--staging-banner-height, 0px) + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  background: ${Colors.White};
  border-radius: 16px;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid ${Colors.BorderLight};
  padding: 0;
  width: 100%;
  min-width: auto;
  max-width: calc(100vw - 32px);
  overflow: visible;
  box-sizing: border-box;

  @media (min-width: ${SCREENS.lg}) {
    max-width: min(1200px, calc(100vw - 24px));
  }
`;

const SearchResultsContainer = styled.div<{ $centered?: boolean }>`
  ${tw`
    flex
    flex-col
    px-4
  `}
  padding-top: calc(1rem / 1.4);
  padding-bottom: calc(1rem / 1.4);
  ${({ $centered }) =>
    $centered &&
    tw`
      items-center
      justify-center
      min-h-full
      w-full
    `}
`;

const ResultsTitle = styled.h2`
  ${tw`
    text-2xl
    font-bold
    text-center
    lg:text-left
    w-full
    mt-0
  `}
  margin-bottom: calc(1.5rem / 1.4);
  line-height: calc(2rem / 1.4);
  color: ${Colors.TextPrimary};
`;

const ResultsCount = styled.span`
  ${tw`
    font-bold
  `}
  color: ${Colors.Gold};
`;

const TabRow = styled.div`
  ${tw`
    flex
    gap-2
    w-full
    mb-4
    justify-center
    lg:justify-start
  `}
`;

const TabButton = styled.button<{ $isActive?: boolean }>`
  ${tw`
    px-4
    py-2
    rounded-full
    text-sm
    font-semibold
    transition-colors
    duration-200
  `}
  background-color: ${props => (props.$isActive ? Colors.Gold : Colors.White)};
  color: ${props => (props.$isActive ? Colors.White : Colors.TextPrimary)};
  border: 1px solid ${props => (props.$isActive ? Colors.Gold : Colors.BorderLight)};

  &:hover {
    border-color: ${Colors.Gold};
  }
`;

const FiltersRow = styled.div`
  ${tw`
    flex
    items-center
    justify-between
    gap-3
    w-full
  `}
  margin-bottom: calc(1rem / 1.4);
`;

const FilterButton = styled.button`
  ${tw`
    px-4
    py-2
    rounded-full
    text-sm
    font-semibold
    transition-colors
    duration-200
  `}
  background: ${Colors.White};
  color: ${Colors.TextPrimary};
  border: 1px solid ${Colors.BorderLight};

  &:hover {
    background: ${Colors.GoldLight};
    border-color: ${Colors.Gold};
  }
`;

const FilterBadge = styled.span`
  ${tw`
    ml-2
    inline-flex
    items-center
    justify-center
    text-xs
    font-bold
    text-white
    rounded-full
    w-5
    h-5
  `}
  background: ${Colors.Gold};
`;

const FilterSection = styled.div`
  ${tw`mb-5`}
`;

const FilterLabel = styled.h4`
  ${tw`text-sm font-semibold text-text-primary mb-2`}
`;

const FilterRow = styled.div`
  ${tw`flex flex-wrap gap-3`}
`;

const FilterSelect = styled.select`
  ${tw`border border-border-default rounded-md px-3 py-2 text-sm w-full`}
`;

const FilterCheckboxLabel = styled.label`
  ${tw`inline-flex items-center gap-2 text-sm text-text-secondary`}
`;

const FuelTypeButton = styled.button<{ $isSelected?: boolean }>`
  ${tw`
    flex
    flex-col
    items-center
    justify-center
    gap-2
    px-4
    py-3
    rounded-lg
    transition-all
    duration-200
    cursor-pointer
    min-w-[120px]
  `}
  background-color: ${props => (props.$isSelected ? Colors.Gold : Colors.White)};
  border: 2px solid ${props => (props.$isSelected ? Colors.Gold : Colors.BorderLight)};
  color: ${props => (props.$isSelected ? Colors.White : Colors.TextPrimary)};

  &:hover {
    border-color: ${Colors.Gold};
  }
`;

const FuelTypeIcon = styled(FontAwesomeIcon)`
  font-size: 20px;
`;

const FuelTypeLabel = styled.span`
  ${tw`text-sm font-medium`}
`;

const FeatureButton = styled.button<{ $isSelected?: boolean }>`
  ${tw`
    flex
    flex-col
    items-center
    justify-center
    gap-2
    px-4
    py-3
    rounded-lg
    transition-all
    duration-200
    cursor-pointer
    min-w-[140px]
  `}
  background-color: ${props => (props.$isSelected ? Colors.Gold : Colors.White)};
  border: 2px solid ${props => (props.$isSelected ? Colors.Gold : Colors.BorderLight)};
  color: ${props => (props.$isSelected ? Colors.White : Colors.TextPrimary)};

  &:hover {
    border-color: ${Colors.Gold};
  }
`;

const FeatureIcon = styled(FontAwesomeIcon)`
  font-size: 18px;
`;

const FeatureLabel = styled.span`
  ${tw`text-sm font-medium text-center`}
`;

const VehicleTypeButton = styled.button<{ $isSelected?: boolean }>`
  ${tw`
    flex
    flex-col
    items-center
    justify-center
    gap-2
    px-4
    py-3
    rounded-lg
    transition-all
    duration-200
    cursor-pointer
    w-[120px]
  `}
  background-color: ${props => (props.$isSelected ? Colors.Gold : Colors.White)};
  border: 2px solid ${props => (props.$isSelected ? Colors.Gold : Colors.BorderLight)};
  color: ${props => (props.$isSelected ? Colors.White : Colors.TextPrimary)};

  &:hover {
    border-color: ${Colors.Gold};
  }
`;

const VehicleTypeIcon = styled(FontAwesomeIcon)`
  font-size: 24px;
`;

const VehicleTypeLabel = styled.span`
  ${tw`text-sm font-medium`}
`;

const fuelTypeIconMap: Record<string, IconDefinition> = {
  GASOLINE: faGasPump,
  HYBRID: faLeaf,
  ELECTRIC: faBolt,
  DIESEL: faOilCan,
};

const featureIconMap: Record<string, IconDefinition> = {
  ALL_WHEEL_DRIVE: faArrowsRotate,
  ANDROID_AUTO: faAndroid,
  APPLE_CARPLAY: faApple,
  AUX_INPUT: faMusic,
  BACKUP_CAMERA: faCamera,
  BIKE_RACK: faBicycle,
  BLIND_SPOT_WARNING: faEye,
  BLUETOOTH: faBluetooth,
  CHILD_SEAT: faChild,
  CONVERTIBLE: faCarSide,
  GPS: faLocationCrosshairs,
  HEATED_SEATS: faFire,
  KEYLESS_ENTRY: faKey,
  PET_FRIENDLY: faPaw,
  SKI_RACK: faBicycle,
  SNOW_TIRES: faSnowflake,
  SUNROOF: faSun,
  TOLL_PASS: faRoad,
  USB_CHARGER: faUsb,
  USB_INPUT: faUsb,
  WHEELCHAIR_ACCESSIBLE: faWheelchair,
};

const FilterRange = styled.input`
  ${tw`w-full`}
  -webkit-appearance: none;
  background: transparent;
  position: absolute;
  pointer-events: none;
  bottom:10px
  height: 16px;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    pointer-events: auto;
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: #ffffff;
    border: 1px solid var(--color-border);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &::-webkit-slider-runnable-track {
    height: 4px;
    background: transparent;
  }

  &::-moz-range-thumb {
    pointer-events: auto;
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: #ffffff;
    border: 1px solid var(--color-border);
  }

  &::-moz-range-track {
    height: 4px;
    background: transparent;
  }
`;

const FilterModalLayout = styled.div`
  ${tw`flex flex-col`}
  height: 100%;
`;

const FilterModalContent = styled.div`
  ${tw`flex-1 overflow-y-auto`}
  padding-right: 4px;
`;

const FilterModalFooter = styled.div`
  ${tw`bg-white`}
  position: sticky;
  bottom: 0;
  padding: 12px 0 max(12px, env(safe-area-inset-bottom));
  border-top: 1px solid ${Colors.BorderLight};
`;

const RangeBar = styled.div`
  ${tw`relative w-full`}
  height: 16px;
`;

const RangeTrack = styled.div`
  ${tw`absolute left-0 right-0`}
  top: 6px;
  height: 6px;
  background: var(--color-border);
  border-radius: 999px;
`;

const RangeSelection = styled.div`
  ${tw`absolute`}
  top: 6px;
  height: 6px;
  background: ${Colors.Gold};
  border-radius: 999px;
`;

const FilterTitle = styled.h3`
  ${tw`text-lg font-semibold text-text-primary mb-4`}
`;

const FilterHelperText = styled.p`
  ${tw`text-sm text-text-secondary mb-2`}
`;

const FilterList = styled.div`
  ${tw`mt-2 text-sm text-text-secondary`}
`;

const FilterLinkButton = styled.button`
  ${tw`underline`}
  color: ${Colors.Gold};
`;

const CardsContainer = styled.div`
  ${tw`
    flex
    flex-col
    justify-center
    w-full
    mx-auto
    box-border
  `}
`;

// Error message components
const ErrorContainer = styled.div`
  ${tw`
    max-w-2xl
    mx-auto
    my-12
    p-8
    text-center
    box-border
  `}
  background: ${Colors.White};
  border: 1px solid ${Colors.BorderLight};
  border-radius: 12px;
`;

const ErrorIcon = styled(FontAwesomeIcon)`
  ${tw`
    text-5xl
    text-red-500
    mb-4
  `}
`;

const WarningIcon = styled(FontAwesomeIcon)`
  ${tw`
    text-5xl
    text-yellow-500
    mb-4
  `}
`;

const ClockIcon = styled(FontAwesomeIcon)`
  ${tw`
    text-5xl
    mb-4
  `}
  color: ${Colors.Gold};
`;

const ErrorTitle = styled.h2`
  ${tw`
    text-2xl
    font-bold
    text-text-primary
    mb-4
  `}
`;

const ErrorMessage = styled.p`
  ${tw`
    text-text-secondary
    mb-6
  `}
`;

const ErrorDetails = styled.div`
  ${tw`
    bg-bg-secondary
    p-4
    rounded-lg
    mb-6
    text-left
    mx-auto
    max-w-md
  `}
`;

const DetailRow = styled.div`
  ${tw`
    flex
    items-center
    mb-2
    text-text-secondary
  `}
`;

const DetailIcon = styled(FontAwesomeIcon)`
  ${tw`
    mr-2
    text-text-secondary
  `}
`;

const ActionButton = styled(Link)`
  ${tw`
    inline-block
    px-6
    py-3
    text-white
    font-medium
    transition-colors
    duration-200
  `}
  background: ${Colors.Gold};
  border-radius: 8px;
  &:hover {
    background: ${Colors.GoldHover};
  }
`;

const SecondaryButton = styled(Link)`
  ${tw`
    inline-block
    px-6
    py-3
    bg-bg-secondary
    text-text-primary
    font-medium
    rounded-md
    transition-colors
    duration-200
    ml-4
  `}
  &:hover {
    ${tw`bg-gray-300`}
  }
`;

const MapButtonContainer = styled.div`
  ${tw`
    fixed
    bottom-6
    right-6
    z-50
  `}
`;

const MapButton = styled.button`
  ${tw`
    flex
    items-center
    gap-2
    px-6
    py-3
    text-white
    font-semibold
    shadow-card-hover
    transition-colors
    duration-200
    cursor-pointer
  `}
  background: ${Colors.Gold};
  border-radius: 8px;
  &:hover {
    background: ${Colors.GoldHover};
  }
`;

const ViewToggleButton = styled.button<{ $raised?: boolean }>`
  ${tw`
    fixed
    right-4
    z-50
    flex
    items-center
    gap-2
    px-5
    py-3
    text-white
    font-semibold
    shadow-card-hover
    transition-[bottom,background-color]
    duration-200
    cursor-pointer
  `}
  bottom: ${props =>
    props.$raised
      ? 'calc(11rem + env(safe-area-inset-bottom, 0px))'
      : 'max(24px, calc(env(safe-area-inset-bottom, 0px) + 16px))'};
  background: ${Colors.Gold};
  border-radius: 8px;
  /* Only show on screens smaller than medium */
  @media (min-width: ${SCREENS.md}) {
    display: none;
  }
  &:hover {
    background: ${Colors.GoldHover};
  }
`;

const Overlay = styled.div<{ $show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 40;
  background: transparent;
  display: ${props => (props.$show ? 'block' : 'none')};
`;

const NoCarsMessage = styled.div`
  ${tw`
    flex
    flex-col
    items-center
    justify-center
    p-8
    bg-white
    rounded-lg
    border-2
    border-dashed
    border-border-default
    text-center
    my-6
  `}
`;

const NoCarsIcon = styled(FontAwesomeIcon)`
  ${tw`
    text-text-tertiary
    mb-4
  `}
  font-size: 3rem;
`;

const NoCarsTitle = styled.h3`
  ${tw`
    text-xl
    font-semibold
    text-text-secondary
    mb-2
  `}
`;

const NoCarsText = styled.p`
  ${tw`
    text-base
    text-text-secondary
    mb-4
  `}
`;

const OffAirportButton = styled.button`
  ${tw`
    px-6
    py-3
    text-white
    font-semibold
    transition-colors
    duration-200
    focus:outline-none
    focus:ring-2
    focus:ring-offset-2
  `}
  background: ${Colors.Gold};
  border-radius: 8px;
  &:hover {
    background: ${Colors.GoldHover};
  }
  &:focus {
    ring-color: ${Colors.Gold};
  }
`;

const MinimizeButton = styled.button`
  ${tw`
    w-full
    py-2
    px-4
    flex
    items-center
    justify-center
    gap-2
    font-medium
    text-sm
    transition-colors
    duration-200
    cursor-pointer
  `}
  background: ${Colors.BgSecondary};
  border-top: 1px solid ${Colors.BorderLight};
  border-radius: 0 0 12px 12px;
  color: ${Colors.TextSecondary};
  &:hover {
    background: ${Colors.GoldLight};
    color: ${Colors.Gold};
  }
`;

const ExpandedSearchWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export function SearchResultPage() {
  const siteConfig = getSiteConfig();
  const hideFilters = !!siteConfig.allowedProductTypes;
  const { search } = useLocation();
  const [selectedCarId, setSelectedCarId] = useState<string | undefined>(undefined);
  const [mapUpdatedCars, setMapUpdatedCars] = useState<any[] | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  );
  const { toggleFavorite, favorites } = useFavorites();
  const isAuthenticated = authStore.isAuthenticated;
  // Shared optimistic favorite state for instant UI updates
  const [localFavoriteIds, setLocalFavoriteIds] = useState<Set<string>>(new Set());

  // Helper to check if a car is favorited (combining all sources)
  // Note: getMyFavorites returns car objects with 'id', while addFavorite returns Favorite objects with 'carId'
  const isCarFavorited = useCallback(
    (carId: string) => {
      return (
        localFavoriteIds.has(carId) ||
        favorites.some((f: any) => f.id === carId || f.carId === carId)
      );
    },
    [localFavoriteIds, favorites],
  );

  // Wrapper for toggleFavorite that also updates local state for optimistic UI
  const handleToggleFavorite = useCallback(
    async (carId: string, isCurrentlyFavorite: boolean) => {
      // Optimistic update
      setLocalFavoriteIds(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyFavorite) {
          newSet.delete(carId);
        } else {
          newSet.add(carId);
        }
        return newSet;
      });
      // Call actual API
      await toggleFavorite(carId, isCurrentlyFavorite);
    },
    [toggleFavorite],
  );
  const searchBarRef = useRef<HTMLDivElement>(null);
  const searchNavSlot = useSearchNavSlot();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<CarSearchFilters>({
    sortBy: 'RELEVANT',
    minPrice: 0,
    maxPrice: 500,
    minMileageLimit: 0,
    unlimitedMileage: false,
    vehicleTypes: [],
    fuelTypes: [],
    features: [],
    makeModels: [],
  });
  const [appliedFilters, setAppliedFilters] = useState<CarSearchFilters>({
    sortBy: 'RELEVANT',
    minPrice: 0,
    maxPrice: 500,
    minMileageLimit: 0,
    unlimitedMileage: false,
    vehicleTypes: [],
    fuelTypes: [],
    features: [],
    makeModels: [],
  });
  const [makeInput, setMakeInput] = useState('');
  const [modelInput, setModelInput] = useState('');
  const currentYear = new Date().getFullYear();

  const toggleView = () => {
    setViewMode(prev => (prev === 'list' ? 'map' : 'list'));
  };

  const toggleSearchExpansion = () => {
    setIsSearchExpanded(prev => !prev);
  };

  const minimizeSearch = () => {
    setIsSearchExpanded(false);
  };

  const toggleMultiSelect = (key: 'vehicleTypes' | 'fuelTypes' | 'features', value: string) => {
    setDraftFilters(prev => {
      const current = prev[key] ?? [];
      const next = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
  };

  const handleAddMakeModel = () => {
    if (!makeInput || !modelInput) {
      return;
    }
    setDraftFilters(prev => {
      const existing = (prev.makeModels ?? []).some(
        pair => pair.make === makeInput && pair.model === modelInput,
      );
      if (existing) {
        return prev;
      }
      return {
        ...prev,
        makeModels: [...(prev.makeModels ?? []), { make: makeInput, model: modelInput }],
      };
    });
  };

  const handleRemoveMakeModel = (index: number) => {
    setDraftFilters(prev => ({
      ...prev,
      makeModels: (prev.makeModels ?? []).filter((_, idx) => idx !== index),
    }));
  };

  const resetFilters = () => {
    const resetState = {
      sortBy: 'RELEVANT',
      minPrice: 0,
      maxPrice: 500,
      minMileageLimit: 0,
      unlimitedMileage: false,
      vehicleTypes: [],
      fuelTypes: [],
      features: [],
      makeModels: [],
    };
    setDraftFilters(resetState);
    setAppliedFilters(resetState);
    setMakeInput('');
    setModelInput('');
  };

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setIsFilterModalOpen(false);
  };

  // Track window width for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle click outside to minimize search panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is inside a portal-safe element (like the date modal)
      const isPortalSafe = target.closest('[data-portal-safe="true"]');
      const isNavSummary = target.closest('[data-search-nav-summary="true"]');

      if (
        isSearchExpanded &&
        searchBarRef.current &&
        !searchBarRef.current.contains(target) &&
        !isPortalSafe &&
        !isNavSummary
      ) {
        minimizeSearch();
      }
    };

    if (isSearchExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isSearchExpanded, minimizeSearch]);

  // Prevent body scroll when search is expanded
  useEffect(() => {
    const lockScroll = isSearchExpanded && windowWidth < SCREENS_PX.md;
    document.body.style.overflow = lockScroll ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isSearchExpanded, windowWidth]);

  useEffect(() => {
    if (isFilterModalOpen) {
      setDraftFilters(appliedFilters);
    }
  }, [isFilterModalOpen, appliedFilters]);

  const {
    locationId,
    returnDate,
    returnLocationId,
    returnTime,
    startTime,
    startDate,
    timezone,
    driverAge,
    type: typeParam,
    searchMode,
    latitude,
    longitude,
    address,
    mode: tripModeParam,
    months: monthsParam,
  } = queryString.parse(search, {
    parseNumbers: true,
    parseBooleans: true,
  });
  // Spec 028: a search picked by months reopens the picker in Months mode.
  const tripPick = parseTripModeParams({ mode: tripModeParam, months: monthsParam });

  const isAddressSearch = searchMode === 'address';

  const { data: makeModelData } = useQuery(GET_CAR_MAKES_AND_MODELS, {
    fetchPolicy: 'cache-first',
  });
  const makeModelOptions = makeModelData?.getAllCarMakesAndModels ?? [];
  const makeOptions = useMemo(() => {
    const makes = new Set<string>();
    makeModelOptions.forEach((entry: { make: string }) => {
      if (entry.make) {
        makes.add(entry.make);
      }
    });
    return Array.from(makes).sort();
  }, [makeModelOptions]);
  const modelOptions = useMemo(() => {
    if (!makeInput) {
      return [];
    }
    const models = new Set<string>();
    makeModelOptions.forEach((entry: { make: string; model: string }) => {
      if (entry.make === makeInput && entry.model) {
        models.add(entry.model);
      }
    });
    return Array.from(models).sort();
  }, [makeInput, makeModelOptions]);
  const selectedYearRange = useMemo(() => {
    if (!makeInput || !modelInput) {
      return undefined;
    }
    const entry = makeModelOptions.find(
      (item: { make: string; model: string }) =>
        item.make === makeInput && item.model === modelInput,
    );
    if (!entry?.years?.length) {
      return undefined;
    }
    const sorted = [...entry.years].sort((a: number, b: number) => a - b);
    return { min: sorted[0], max: sorted[sorted.length - 1] };
  }, [makeInput, modelInput, makeModelOptions]);

  const yearBounds = useMemo(() => {
    if (selectedYearRange) {
      return selectedYearRange;
    }
    return { min: 1990, max: currentYear + 2 };
  }, [selectedYearRange, currentYear]);

  const getPercent = (value: number, min: number, max: number) => {
    if (max === min) {
      return 0;
    }
    return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.sortBy && appliedFilters.sortBy !== 'RELEVANT') count += 1;
    if (appliedFilters.minPrice && appliedFilters.minPrice > 0) count += 1;
    if (appliedFilters.maxPrice && appliedFilters.maxPrice < 500) count += 1;
    if (appliedFilters.vehicleTypes && appliedFilters.vehicleTypes.length) count += 1;
    if (appliedFilters.fuelTypes && appliedFilters.fuelTypes.length) count += 1;
    if (appliedFilters.features && appliedFilters.features.length) count += 1;
    if (appliedFilters.unlimitedMileage) count += 1;
    if (appliedFilters.minMileageLimit && appliedFilters.minMileageLimit > 0) count += 1;
    if (appliedFilters.makeModels && appliedFilters.makeModels.length) count += 1;
    if (appliedFilters.minYear) count += 1;
    if (appliedFilters.maxYear) count += 1;
    if (appliedFilters.minSeats) count += 1;
    if (appliedFilters.transmission) count += 1;
    return count;
  }, [appliedFilters]);

  // Reset mapUpdatedCars when search parameters change (new search initiated)
  useEffect(() => {
    // Reset to show initial query results for new search
    setMapUpdatedCars(null);
  }, [locationId, latitude, longitude, startDate, returnDate, startTime, returnTime]);

  useEffect(() => {
    setMapUpdatedCars(null);
  }, [appliedFilters]);

  const resolvedTypeParam =
    Array.isArray(typeParam) && typeParam.length > 0 ? typeParam[0] : typeParam;
  // Normalize the type parameter to uppercase to match ProductType enum values
  const normalizedTypeParam =
    typeof resolvedTypeParam === 'string' ? resolvedTypeParam.toUpperCase() : null;
  const selectedProductType =
    normalizedTypeParam && Object.values(ProductType).includes(normalizedTypeParam as ProductType)
      ? (normalizedTypeParam as ProductType)
      : ProductType.Car;

  // Create search params object to pass to BookCard
  const searchParams = {
    locationId: Number(locationId),
    returnLocationId: returnLocationId ? Number(returnLocationId) : undefined,
    startDate: startDate ? new Date(String(startDate)) : undefined,
    returnDate: returnDate ? new Date(String(returnDate)) : undefined,
    startTime: String(startTime),
    returnTime: String(returnTime),
    driverAge: driverAge ? Number(driverAge) : undefined,
    type: selectedProductType,
  };

  const startDateTime = combinePickerDateTimeGetUTC(
    new Date(String(startDate)),
    String(startTime),
    String(timezone),
  );

  const endDateTime = combinePickerDateTimeGetUTC(
    new Date(String(returnDate)),
    String(returnTime),
    String(timezone),
  );

  const apiFilters = useMemo(() => {
    const cleaned: CarSearchFilters = {
      sortBy: appliedFilters.sortBy,
      minPrice:
        appliedFilters.minPrice && appliedFilters.minPrice > 0
          ? appliedFilters.minPrice
          : undefined,
      maxPrice:
        appliedFilters.maxPrice !== undefined && appliedFilters.maxPrice < 500
          ? appliedFilters.maxPrice
          : undefined,
      vehicleTypes: appliedFilters.vehicleTypes?.length ? appliedFilters.vehicleTypes : undefined,
      fuelTypes: appliedFilters.fuelTypes?.length ? appliedFilters.fuelTypes : undefined,
      features: appliedFilters.features?.length ? appliedFilters.features : undefined,
      minMileageLimit:
        appliedFilters.minMileageLimit && appliedFilters.minMileageLimit > 0
          ? appliedFilters.minMileageLimit
          : undefined,
      unlimitedMileage: appliedFilters.unlimitedMileage || undefined,
      makeModels: appliedFilters.makeModels?.length ? appliedFilters.makeModels : undefined,
      minYear: appliedFilters.minYear || undefined,
      maxYear: appliedFilters.maxYear || undefined,
      minSeats: appliedFilters.minSeats || undefined,
      transmission: appliedFilters.transmission || undefined,
      isAddressSearch,
    };
    return cleaned;
  }, [appliedFilters, isAddressSearch]);

  const [
    getLocation,
    { data: locationInfoData, loading: locationInfoLoading, error: locationInfoError },
  ] = useGetLocationLazyQuery({});

  const [activeTab, setActiveTab] = useState<AirportSearchTab>('offAirport');

  // Determine if we should use horizontal layout (for large screens)
  // Use horizontal layout on screens >= 1024px (lg breakpoint)
  const shouldUseHorizontalLayout = windowWidth >= 1024;

  const deliverySearchInput = {
    locationId: isAddressSearch ? undefined : Number(locationId),
    latitude: isAddressSearch && latitude ? Number(latitude) : undefined,
    longitude: isAddressSearch && longitude ? Number(longitude) : undefined,
    radiusMiles: isAddressSearch ? 20 : undefined,
    startAt: startDateTime, // Already in seconds
    endAt: endDateTime, // Already in seconds
    driverAge: Number(driverAge),
    type: selectedProductType,
    filters: apiFilters,
    siteHost: getCurrentSiteHost() || undefined,
  };

  const {
    data: deliverySearchData,
    loading: deliverySearchLoading,
    error: deliverySearchError,
  } = useQuery<AvailableCarsWithDeliveryResponse>(FIND_AVAILABLE_CARS_WITH_DELIVERY, {
    fetchPolicy: 'network-only',
    variables: { input: deliverySearchInput },
    skip: isAddressSearch ? !latitude || !longitude : !locationId,
  });

  // Ensure we fetch location details if we have a locationId, regardless of search success
  useEffect(() => {
    if (locationId && !isAddressSearch) {
      getLocation({ variables: { id: Number(locationId) } });
    }
  }, [locationId, isAddressSearch, getLocation]);

  // Handle delivery search error - fetch location if search fails
  useEffect(() => {
    if (deliverySearchError && !isAddressSearch && locationId) {
      getLocation({ variables: { id: Number(locationId) } });
    }
  }, [deliverySearchError, isAddressSearch, locationId, getLocation]);

  const loading = deliverySearchLoading;
  const error = deliverySearchError;
  const airportSearch = deliverySearchData?.availableCarsWithDelivery;
  const isAirportSearch = Boolean(airportSearch?.isAirportSearch);
  const airportContext = airportSearch?.airportContext || null;
  const atAirportCars = airportSearch?.atAirportCars || [];
  const offAirportCars = airportSearch?.offAirportCars || [];
  const airportAddress = airportContext ? airportContext.name : '';

  useEffect(() => {
    if (isAirportSearch) {
      setActiveTab('airport');
    } else {
      setActiveTab('offAirport');
    }
  }, [isAirportSearch]);

  // Get location name for compact view
  const getLocationName = () => {
    if (isAirportSearch && activeTab === 'airport' && airportContext) {
      return airportContext.name || formatAddress(airportContext as any);
    }
    if (isAddressSearch && address) {
      return String(address);
    }
    if (locationInfoData?.location) {
      const loc = locationInfoData.location;
      return loc.name || formatAddress(loc);
    }
    if (locationId) {
      return 'Loading location...';
    }
    return 'Select Location';
  };

  // Helper function to get cars array from either search mode
  const getCars = () => {
    // If map has updated cars (from "Search this area"), use those
    // This includes empty arrays (search returned no results)
    if (mapUpdatedCars !== null && (!isAirportSearch || activeTab === 'offAirport')) {
      return mapUpdatedCars;
    }
    if (!deliverySearchData) {
      return [];
    }
    if (isAirportSearch) {
      return activeTab === 'airport' ? atAirportCars : offAirportCars;
    }
    return offAirportCars;
  };

  const cars = getCars();

  // Spec 028 "similar dates": cars free for the same trip moved a few days, below the list.
  // It follows the searched dates, so it hides after "Search this area" and on the
  // At airport tab (the server covers the off-airport list only).
  const similarDatesUrlParams = (shiftDays: number) =>
    queryString.stringify({
      locationId: isAddressSearch ? undefined : Number(locationId),
      returnLocationId: returnLocationId ? Number(returnLocationId) : undefined,
      startDate: shiftPickerDate(String(startDate), shiftDays),
      startTime,
      returnDate: shiftPickerDate(String(returnDate), shiftDays),
      returnTime,
      driverAge,
      searchMode: isAddressSearch ? 'address' : 'location',
      address: isAddressSearch ? String(address) : undefined,
      latitude: isAddressSearch && latitude ? Number(latitude) : undefined,
      longitude: isAddressSearch && longitude ? Number(longitude) : undefined,
      timezone: isAddressSearch ? String(timezone) : undefined,
      mode: tripPick.mode === 'months' ? 'months' : undefined,
      months: tripPick.mode === 'months' ? tripPick.months : undefined,
    });
  const showSimilarDates =
    !error &&
    !loading &&
    Boolean(deliverySearchData) &&
    mapUpdatedCars === null &&
    !(isAirportSearch && activeTab === 'airport') &&
    Number.isFinite(startDateTime) &&
    Number.isFinite(endDateTime) &&
    endDateTime > startDateTime;
  const similarDatesSection = showSimilarDates ? (
    <SimilarDatesSection
      key={search}
      input={deliverySearchInput}
      totalDays={Math.ceil((endDateTime - startDateTime) / 86_400_000)}
      startDate={String(startDate)}
      returnDate={String(returnDate)}
      defaultOn={offAirportCars.length === 0}
      urlParamsFor={similarDatesUrlParams}
    />
  ) : null;

  // Convert cars to Map component format and filter out cars without valid coordinates
  // Priority: car.latitude/longitude first, then fallback to car.location.latitude/longitude
  const mapCars: Car[] = cars
    .map((car: any) => {
      // Get coordinates: prefer car.latitude/longitude, fallback to location
      const latitude = car.latitude ?? car.location?.latitude;
      const longitude = car.longitude ?? car.location?.longitude;

      return {
        id: car.id,
        make: car.make,
        model: car.model,
        year: car.year,
        location: {
          latitude: latitude,
          longitude: longitude,
          address: car.location?.address || '',
          city: car.location?.city || '',
          state: car.location?.state || '',
        },
        weekdayPrice: car.weekdayPrice,
        thumbnailUrl: car.images?.[0]?.imageUrl || car.thumbnailUrl,
        calculatedPrice: car.calculatedPrice,
        distanceMiles: car.distanceMiles,
        // Store direct fields for map rendering (use location coordinates)
        latitude: latitude,
        longitude: longitude,
      };
    })
    .filter((car: Car) => {
      const lat = (car as any).latitude;
      const lng = (car as any).longitude;
      const isValid =
        lat &&
        lng &&
        !isNaN(Number(lat)) &&
        !isNaN(Number(lng)) &&
        Number(lat) !== 0 &&
        Number(lng) !== 0;

      if (!isValid) {
      }

      return isValid;
    });

  const handleCarSelect = useCallback((car: Car | null) => {
    setSelectedCarId(car?.id);
    if (!car) return;
    const element = document.getElementById(`car-card-${car.id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const handleCarCardClick = useCallback((carId: string) => {
    setSelectedCarId(carId);
  }, []);

  const bookCardSearchParams = {
    locationId: isAddressSearch ? undefined : Number(locationId),
    returnLocationId: returnLocationId ? Number(returnLocationId) : undefined,
    startDate: startDate ? new Date(String(startDate)) : undefined,
    returnDate: returnDate ? new Date(String(returnDate)) : undefined,
    startTime: String(startTime),
    returnTime: String(returnTime),
    driverAge: Number(driverAge),
    type: selectedProductType,
    searchMode: (isAddressSearch ? 'address' : 'location') as 'address' | 'location',
    address: isAddressSearch ? String(address) : undefined,
    latitude: isAddressSearch && latitude ? Number(latitude) : undefined,
    longitude: isAddressSearch && longitude ? Number(longitude) : undefined,
    timezone: isAddressSearch ? String(timezone) : undefined,
    tripMode: tripPick.mode,
    months: tripPick.months,
  };

  const searchChrome = (
    <>
      {searchNavSlot &&
        createPortal(
          <SearchNavSummary
            locationName={getLocationName()}
            startDate={startDate ? new Date(String(startDate)) : null}
            startTime={startTime ? String(startTime) : null}
            returnDate={returnDate ? new Date(String(returnDate)) : null}
            returnTime={returnTime ? String(returnTime) : null}
            driverAge={driverAge ? Number(driverAge) : 25}
            showFilters={!hideFilters}
            onEdit={toggleSearchExpansion}
            onOpenFilters={() => setIsFilterModalOpen(true)}
          />,
          searchNavSlot,
        )}
      {isSearchExpanded && (
        <>
          <Overlay $show />
          <SearchBarContainer ref={searchBarRef}>
            <ExpandedSearchWrapper>
              <BookCard
                absolute={false}
                locationSectionHidden={false}
                horizontal={shouldUseHorizontalLayout}
                onSearchComplete={minimizeSearch}
                syncUrlOnChange
                initialSearchParams={bookCardSearchParams}
              />
              {windowWidth < SCREENS_PX.md && (
                <MinimizeButton onClick={minimizeSearch}>
                  <FontAwesomeIcon icon={faChevronUp} />
                  <span>Minimize</span>
                </MinimizeButton>
              )}
            </ExpandedSearchWrapper>
          </SearchBarContainer>
        </>
      )}
    </>
  );

  if (loading) {
    return (
      <>
        {searchChrome}
        <SearchResultsSkeleton />
      </>
    );
  }
  // Handle different error types
  if (error || (!deliverySearchData && !loading)) {
    // Check if it's an operating hours error
    const errorCode = error?.graphQLErrors?.[0]?.extensions?.code;

    if (errorCode === ErrorCode.LocationOutOfOperatingHours) {
      return (
        <PageContainer>
          {searchChrome}

          <SearchResultsContainer $centered>
            <ErrorContainer>
              <ClockIcon icon={faClock} />
              <ErrorTitle>Location Closed During Selected Hours</ErrorTitle>
              <ErrorMessage>
                The location you selected is not open during your requested pickup or return time.
                Please adjust your times or choose a different location.
              </ErrorMessage>

              <ErrorDetails>
                <DetailRow>
                  <DetailIcon icon={faMapMarkerAlt} />
                  <span>Location: {formatAddress(locationInfoData?.location)}</span>
                </DetailRow>
                <DetailRow>
                  <DetailIcon icon={faCalendarAlt} />
                  <span>Pickup: {new Date(startDateTime).toLocaleString()}</span>
                </DetailRow>
                <DetailRow>
                  <DetailIcon icon={faCalendarAlt} />
                  <span>Return: {new Date(endDateTime).toLocaleString()}</span>
                </DetailRow>
              </ErrorDetails>
            </ErrorContainer>
          </SearchResultsContainer>
        </PageContainer>
      );
    }

    // Generic error for other cases
    return (
      <PageContainer>
        {searchChrome}

        <SearchResultsContainer $centered>
          <ErrorContainer>
            <ErrorIcon icon={faExclamationCircle} />
            <ErrorTitle>Unable to Find Available Cars</ErrorTitle>
            <ErrorMessage>
              {error?.message ||
                'We encountered an issue while searching for available cars. Please try again later.'}
            </ErrorMessage>
          </ErrorContainer>
        </SearchResultsContainer>
      </PageContainer>
    );
  }

  if (
    cars.length === 0 &&
    (!isAirportSearch || (atAirportCars.length === 0 && offAirportCars.length === 0))
  ) {
    // On medium+ screens, show no cars message in left panel with map on right
    // On small screens, show full-page centered message (current behavior)
    const isMediumOrLarger = windowWidth >= 768;

    if (isMediumOrLarger) {
      // Show left panel with no cars message + map on right
      return (
        <PageContainer>
          {searchChrome}

          <LeftPanel $show={viewMode === 'list'} $isSearchExpanded={isSearchExpanded}>
            <SearchResultsContainer>
              <ResultsTitle>
                Found <ResultsCount>0</ResultsCount> cars available
              </ResultsTitle>

              {!hideFilters && (
                <FiltersRow>
                  <FilterButton type="button" onClick={() => setIsFilterModalOpen(true)}>
                    Filters
                    {activeFilterCount > 0 ? <FilterBadge>{activeFilterCount}</FilterBadge> : null}
                  </FilterButton>
                  {activeFilterCount > 0 ? (
                    <FilterButton type="button" onClick={resetFilters}>
                      Reset
                    </FilterButton>
                  ) : null}
                </FiltersRow>
              )}

              <NoCarsMessage>
                <WarningIcon icon={faExclamationTriangle} />
                <NoCarsTitle>No Cars Available</NoCarsTitle>
                <NoCarsText>
                  {activeFilterCount > 0
                    ? 'Try changing your filters or resetting them to see all cars.'
                    : "We couldn't find any cars available for your selected dates and location. Please try different dates or another location."}
                </NoCarsText>
                {activeFilterCount > 0 && (
                  <OffAirportButton type="button" onClick={resetFilters}>
                    Reset Filters
                  </OffAirportButton>
                )}
              </NoCarsMessage>
              {similarDatesSection}
            </SearchResultsContainer>
          </LeftPanel>

          <RightPanel $show={viewMode === 'map'}>
            <Map
              cars={[]}
              searchParams={{
                latitude: isAddressSearch
                  ? Number(latitude)
                  : locationInfoData?.location?.latitude || 0,
                longitude: isAddressSearch
                  ? Number(longitude)
                  : locationInfoData?.location?.longitude || 0,
                startDate: new Date(String(startDate)),
                endDate: new Date(String(returnDate)),
                startTime: String(startTime),
                returnTime: String(returnTime),
                timezone: String(timezone || 'America/Los_Angeles'),
                driverAge: Number(driverAge),
                type: selectedProductType,
                locationId: Number(locationId),
                returnLocationId: returnLocationId ? Number(returnLocationId) : undefined,
              }}
              onCarsUpdate={updatedCars => {
                setMapUpdatedCars(updatedCars);
              }}
              onCarSelect={handleCarSelect}
              selectedCarId={selectedCarId}
              favorites={favorites}
              onToggleFavorite={handleToggleFavorite}
              isCarFavorited={isCarFavorited}
            />
          </RightPanel>

          <Modal
            isOpen={isFilterModalOpen}
            onClose={() => setIsFilterModalOpen(false)}
            modalWidth="min(760px, 95vw)"
            modalHeight="min(90vh, 900px)"
          >
            <FilterModalLayout>
              <FilterModalContent>
                <FilterTitle>All filters</FilterTitle>

                <FilterSection>
                  <FilterLabel>Sort by</FilterLabel>
                  <FilterSelect
                    value={draftFilters.sortBy}
                    onChange={event =>
                      setDraftFilters(prev => ({ ...prev, sortBy: event.target.value }))
                    }
                  >
                    {SORT_OPTIONS.filter(
                      option => isAddressSearch || option.value !== 'DISTANCE',
                    ).map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FilterSelect>
                </FilterSection>

                <FilterSection>
                  <FilterLabel>Daily price</FilterLabel>
                  <FilterHelperText>
                    ${draftFilters.minPrice ?? 0} - $
                    {draftFilters.maxPrice && draftFilters.maxPrice >= 500
                      ? '500+'
                      : (draftFilters.maxPrice ?? 500)}
                    /day
                  </FilterHelperText>
                  <RangeBar>
                    <RangeTrack />
                    <RangeSelection
                      style={{
                        left: `${getPercent(draftFilters.minPrice ?? 0, 0, 500)}%`,
                        right: `${100 - getPercent(draftFilters.maxPrice ?? 500, 0, 500)}%`,
                      }}
                    />
                    <FilterRange
                      type="range"
                      min={0}
                      max={500}
                      step={50}
                      value={draftFilters.minPrice ?? 0}
                      onChange={event =>
                        setDraftFilters(prev => {
                          const minPrice = Number(event.target.value);
                          const maxPrice = prev.maxPrice ?? 500;
                          return {
                            ...prev,
                            minPrice,
                            maxPrice: Math.max(maxPrice, minPrice),
                          };
                        })
                      }
                    />
                    <FilterRange
                      type="range"
                      min={0}
                      max={500}
                      step={50}
                      value={draftFilters.maxPrice ?? 500}
                      onChange={event =>
                        setDraftFilters(prev => {
                          const maxPrice = Number(event.target.value);
                          const minPrice = prev.minPrice ?? 0;
                          return {
                            ...prev,
                            minPrice: Math.min(minPrice, maxPrice),
                            maxPrice,
                          };
                        })
                      }
                    />
                  </RangeBar>
                </FilterSection>

                <FilterSection>
                  <FilterLabel>Vehicle type</FilterLabel>
                  <FilterRow>
                    {VEHICLE_TYPE_OPTIONS.map(option => {
                      const isSelected = draftFilters.vehicleTypes?.includes(option.value) ?? false;
                      return (
                        <VehicleTypeButton
                          key={option.value}
                          type="button"
                          $isSelected={isSelected}
                          onClick={() => toggleMultiSelect('vehicleTypes', option.value)}
                        >
                          <VehicleIcon
                            type={option.value}
                            size={48}
                            color={isSelected ? '#ffffff' : 'var(--color-text-primary)'}
                          />
                          <VehicleTypeLabel>{option.label}</VehicleTypeLabel>
                        </VehicleTypeButton>
                      );
                    })}
                  </FilterRow>
                </FilterSection>
              </FilterModalContent>
              <FilterModalFooter>
                <FilterRow>
                  <FilterButton type="button" onClick={() => setIsFilterModalOpen(false)}>
                    Cancel
                  </FilterButton>
                  <FilterButton type="button" onClick={resetFilters}>
                    Reset
                  </FilterButton>
                  <FilterButton type="button" onClick={applyFilters}>
                    Apply filters
                  </FilterButton>
                </FilterRow>
              </FilterModalFooter>
            </FilterModalLayout>
          </Modal>

          <ViewToggleButton
            onClick={toggleView}
            $raised={viewMode === 'map' && Boolean(selectedCarId)}
          >
            <FontAwesomeIcon icon={viewMode === 'list' ? faMap : faList} />
            <span>{viewMode === 'list' ? 'Map' : 'List'}</span>
          </ViewToggleButton>
        </PageContainer>
      );
    }

    // Small screens: show full-page centered message
    return (
      <PageContainer>
        {searchChrome}

        <SearchResultsContainer $centered>
          <ErrorContainer>
            <WarningIcon icon={faExclamationTriangle} />
            <ErrorTitle>No Cars Available</ErrorTitle>
            <ErrorMessage>
              {activeFilterCount > 0
                ? 'Try changing your filters or resetting them to see all cars.'
                : "We couldn't find any cars available for your selected dates and location. Please try different dates or another location."}
            </ErrorMessage>
            {activeFilterCount > 0 && (
              <FilterButton type="button" onClick={resetFilters}>
                Reset filters
              </FilterButton>
            )}
            {activeFilterCount === 0 && (
              <ErrorDetails>
                <DetailRow>
                  <DetailIcon icon={faMapMarkerAlt} />
                  <span>Location ID: {locationId}</span>
                </DetailRow>
                <DetailRow>
                  <DetailIcon icon={faCalendarAlt} />
                  <span>Pickup: {new Date(startDateTime).toLocaleString()}</span>
                </DetailRow>
                <DetailRow>
                  <DetailIcon icon={faCalendarAlt} />
                  <span>Return: {new Date(endDateTime).toLocaleString()}</span>
                </DetailRow>
              </ErrorDetails>
            )}
          </ErrorContainer>
          {similarDatesSection}
        </SearchResultsContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {searchChrome}

      <LeftPanel $show={viewMode === 'list'} $isSearchExpanded={isSearchExpanded}>
        <SearchResultsContainer>
          <ResultsTitle>
            Found <ResultsCount>{cars.length}</ResultsCount> cars available
          </ResultsTitle>

          {!hideFilters && (
            <FiltersRow>
              <FilterButton type="button" onClick={() => setIsFilterModalOpen(true)}>
                Filters
                {activeFilterCount > 0 ? <FilterBadge>{activeFilterCount}</FilterBadge> : null}
              </FilterButton>
              {activeFilterCount > 0 ? (
                <FilterButton type="button" onClick={resetFilters}>
                  Reset
                </FilterButton>
              ) : null}
            </FiltersRow>
          )}

          {isAirportSearch ? (
            <TabRow>
              <TabButton
                type="button"
                $isActive={activeTab === 'airport'}
                onClick={() => setActiveTab('airport')}
              >
                At airport ({atAirportCars.length})
              </TabButton>
              <TabButton
                type="button"
                $isActive={activeTab === 'offAirport'}
                onClick={() => setActiveTab('offAirport')}
              >
                Off airport ({offAirportCars.length})
              </TabButton>
            </TabRow>
          ) : null}

          <CardsContainer>
            {isAirportSearch && activeTab === 'airport' && atAirportCars.length === 0 ? (
              <NoCarsMessage>
                <NoCarsIcon icon={faMapMarkerAlt} />
                <NoCarsTitle>No cars available at this airport</NoCarsTitle>
                <NoCarsText>
                  No cars deliver to the {airportContext?.name || 'selected'} airport.
                  <br />
                  Try selecting "Off airport" to see available cars nearby.
                </NoCarsText>
                <OffAirportButton onClick={() => setActiveTab('offAirport')}>
                  View Off Airport Cars ({offAirportCars.length})
                </OffAirportButton>
              </NoCarsMessage>
            ) : null}
            {cars.map((car: any) => (
              <div
                key={car.id}
                id={`car-card-${car.id}`}
                onClick={() => handleCarCardClick(car.id)}
                style={{
                  cursor: 'pointer',
                  backgroundColor:
                    selectedCarId === car.id ? 'var(--color-bg-secondary)' : 'transparent',
                  transition: 'background-color 0.2s',
                }}
              >
                <CarCard
                  id={car.id}
                  name={car.make ?? ''}
                  thumbnailUrl={car.images?.[0]?.imageUrl ?? ''}
                  location={
                    isAirportSearch && activeTab === 'airport' && airportContext?.name
                      ? airportContext.name
                      : car.location.city
                  }
                  weekdayPrice={car.weekdayPrice ?? 0}
                  isNew={car.isNew ?? false}
                  distanceMiles={car.distanceMiles}
                  urlParams={queryString.stringify({
                    locationId:
                      isAirportSearch && activeTab === 'airport' && airportContext
                        ? airportContext.id
                        : isAddressSearch
                          ? undefined
                          : Number(locationId),
                    returnLocationId:
                      isAirportSearch && activeTab === 'airport' && airportContext
                        ? returnLocationId
                          ? Number(returnLocationId)
                          : airportContext.id
                        : returnLocationId
                          ? Number(returnLocationId)
                          : undefined,
                    startDate,
                    startTime,
                    returnDate,
                    returnTime,
                    driverAge,
                    searchMode:
                      isAirportSearch && activeTab === 'airport'
                        ? 'location'
                        : isAddressSearch
                          ? 'address'
                          : 'location',
                    address:
                      isAirportSearch && activeTab === 'airport'
                        ? undefined
                        : isAddressSearch
                          ? String(address)
                          : undefined,
                    latitude:
                      isAirportSearch && activeTab === 'airport'
                        ? undefined
                        : isAddressSearch && latitude
                          ? Number(latitude)
                          : undefined,
                    longitude:
                      isAirportSearch && activeTab === 'airport'
                        ? undefined
                        : isAddressSearch && longitude
                          ? Number(longitude)
                          : undefined,
                    timezone:
                      isAirportSearch && activeTab === 'airport'
                        ? (airportContext?.timezone ?? undefined)
                        : isAddressSearch
                          ? String(timezone)
                          : undefined,
                  })}
                  calculatedPrice={car.calculatedPrice}
                  showDeliveryFee={isAirportSearch && activeTab === 'airport'}
                  isFavorite={isCarFavorited(car.id)}
                  onFavoriteClick={
                    isAuthenticated
                      ? () => handleToggleFavorite(car.id, isCarFavorited(car.id))
                      : undefined
                  }
                />
              </div>
            ))}
          </CardsContainer>
          {similarDatesSection}
        </SearchResultsContainer>
      </LeftPanel>

      <RightPanel $show={viewMode === 'map'}>
        <Map
          cars={mapCars}
          searchParams={{
            latitude:
              isAirportSearch && activeTab === 'airport' && airportContext?.latitude
                ? Number(airportContext.latitude)
                : isAddressSearch
                  ? Number(latitude)
                  : locationInfoData?.location?.latitude || 0,
            longitude:
              isAirportSearch && activeTab === 'airport' && airportContext?.longitude
                ? Number(airportContext.longitude)
                : isAddressSearch
                  ? Number(longitude)
                  : locationInfoData?.location?.longitude || 0,
            startDate: new Date(String(startDate)),
            endDate: new Date(String(returnDate)),
            startTime: String(startTime),
            returnTime: String(returnTime),
            timezone: String(
              (isAirportSearch && activeTab === 'airport' && airportContext?.timezone) ||
                timezone ||
                'America/Los_Angeles',
            ),
            driverAge: Number(driverAge),
            type: selectedProductType,
            locationId:
              isAirportSearch && activeTab === 'airport' && airportContext
                ? airportContext.id
                : Number(locationId),
            returnLocationId:
              isAirportSearch && activeTab === 'airport' && airportContext
                ? returnLocationId
                  ? Number(returnLocationId)
                  : airportContext.id
                : returnLocationId
                  ? Number(returnLocationId)
                  : undefined,
            focusRadiusMiles: isAirportSearch && activeTab === 'airport' ? 3 : undefined,
            lockToSearchLocation: isAirportSearch && activeTab === 'airport',
          }}
          onCarsUpdate={updatedCars => {
            // Update the cars list on the left panel when map search returns new results
            setMapUpdatedCars(updatedCars);
          }}
          onCarSelect={handleCarSelect}
          selectedCarId={selectedCarId}
          favorites={favorites}
          onToggleFavorite={handleToggleFavorite}
          isCarFavorited={isCarFavorited}
        />
      </RightPanel>

      <Modal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        modalWidth="min(760px, 95vw)"
        modalHeight="min(90vh, 900px)"
      >
        <FilterModalLayout>
          <FilterModalContent>
            <FilterTitle>All filters</FilterTitle>

            <FilterSection>
              <FilterLabel>Sort by</FilterLabel>
              <FilterSelect
                value={draftFilters.sortBy}
                onChange={event =>
                  setDraftFilters(prev => ({ ...prev, sortBy: event.target.value }))
                }
              >
                {SORT_OPTIONS.filter(option => isAddressSearch || option.value !== 'DISTANCE').map(
                  option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ),
                )}
              </FilterSelect>
            </FilterSection>

            <FilterSection>
              <FilterLabel>Daily price</FilterLabel>
              <FilterHelperText>
                ${draftFilters.minPrice ?? 0} - $
                {draftFilters.maxPrice && draftFilters.maxPrice >= 500
                  ? '500+'
                  : (draftFilters.maxPrice ?? 500)}
                /day
              </FilterHelperText>
              <RangeBar>
                <RangeTrack />
                <RangeSelection
                  style={{
                    left: `${getPercent(draftFilters.minPrice ?? 0, 0, 500)}%`,
                    right: `${100 - getPercent(draftFilters.maxPrice ?? 500, 0, 500)}%`,
                  }}
                />
                <FilterRange
                  type="range"
                  min={0}
                  max={500}
                  step={50}
                  value={draftFilters.minPrice ?? 0}
                  onChange={event =>
                    setDraftFilters(prev => {
                      const minPrice = Number(event.target.value);
                      const maxPrice = prev.maxPrice ?? 500;
                      return {
                        ...prev,
                        minPrice,
                        maxPrice: Math.max(maxPrice, minPrice),
                      };
                    })
                  }
                />
                <FilterRange
                  type="range"
                  min={0}
                  max={500}
                  step={50}
                  value={draftFilters.maxPrice ?? 500}
                  onChange={event =>
                    setDraftFilters(prev => {
                      const maxPrice = Number(event.target.value);
                      const minPrice = prev.minPrice ?? 0;
                      return {
                        ...prev,
                        minPrice: Math.min(minPrice, maxPrice),
                        maxPrice,
                      };
                    })
                  }
                />
              </RangeBar>
            </FilterSection>

            <FilterSection>
              <FilterLabel>Vehicle type</FilterLabel>
              <FilterRow>
                {VEHICLE_TYPE_OPTIONS.map(option => {
                  const isSelected = draftFilters.vehicleTypes?.includes(option.value) ?? false;
                  // Map vehicle types to icons
                  return (
                    <VehicleTypeButton
                      key={option.value}
                      type="button"
                      $isSelected={isSelected}
                      onClick={() => toggleMultiSelect('vehicleTypes', option.value)}
                    >
                      <VehicleIcon
                        type={option.value}
                        size={48}
                        color={isSelected ? '#ffffff' : 'var(--color-text-primary)'}
                      />
                      <VehicleTypeLabel>{option.label}</VehicleTypeLabel>
                    </VehicleTypeButton>
                  );
                })}
              </FilterRow>
            </FilterSection>

            <FilterSection>
              <FilterLabel>Number of seats</FilterLabel>
              <FilterSelect
                value={draftFilters.minSeats ?? ''}
                onChange={event =>
                  setDraftFilters(prev => ({
                    ...prev,
                    minSeats: event.target.value ? Number(event.target.value) : undefined,
                  }))
                }
              >
                <option value="">Select</option>
                {SEAT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FilterSelect>
            </FilterSection>

            <FilterSection>
              <FilterSection>
                <FilterLabel>Fuel type</FilterLabel>
                <FilterRow>
                  {FUEL_TYPE_OPTIONS.map(option => {
                    const isSelected = draftFilters.fuelTypes?.includes(option.value) ?? false;
                    const icon = fuelTypeIconMap[option.value] ?? faGasPump;

                    return (
                      <FuelTypeButton
                        key={option.value}
                        type="button"
                        $isSelected={isSelected}
                        aria-pressed={isSelected}
                        onClick={() => toggleMultiSelect('fuelTypes', option.value)}
                      >
                        <FuelTypeIcon icon={icon} />
                        <FuelTypeLabel>{option.label}</FuelTypeLabel>
                      </FuelTypeButton>
                    );
                  })}
                </FilterRow>
              </FilterSection>

              <FilterSection>
                <FilterLabel>Features</FilterLabel>
                <FilterRow>
                  {FEATURE_OPTIONS.map(option => {
                    const isSelected = draftFilters.features?.includes(option.value) ?? false;
                    const icon = featureIconMap[option.value] ?? faCircleCheck;

                    return (
                      <FeatureButton
                        key={option.value}
                        type="button"
                        $isSelected={isSelected}
                        aria-pressed={isSelected}
                        onClick={() => toggleMultiSelect('features', option.value)}
                      >
                        <FeatureIcon icon={icon} />
                        <FeatureLabel>{option.label}</FeatureLabel>
                      </FeatureButton>
                    );
                  })}
                </FilterRow>
              </FilterSection>

              {/* <FilterSection>
            <FilterLabel>Mileage included</FilterLabel>
            <FilterRow>
              <FilterCheckboxLabel>
                <input
                  type="checkbox"
                  checked={draftFilters.unlimitedMileage ?? false}
                  onChange={event =>
                    setDraftFilters(prev => ({ ...prev, unlimitedMileage: event.target.checked }))
                  }
                />
                Unlimited
              </FilterCheckboxLabel>
            </FilterRow>
            <FilterHelperText>
              {draftFilters.minMileageLimit ?? 0} mi/day and more
            </FilterHelperText>
            <FilterRange
              type="range"
              min={0}
              max={500}
              step={25}
              value={draftFilters.minMileageLimit ?? 0}
              onChange={event =>
                setDraftFilters(prev => ({ ...prev, minMileageLimit: Number(event.target.value) }))
              }
            />
          </FilterSection> */}

              {/* <FilterSection>
            <FilterLabel>Make & model</FilterLabel>
            <FilterRow>
              <FilterSelect
                value={makeInput}
                onChange={event => {
                  setMakeInput(event.target.value);
                  setModelInput('');
                }}
              >
                <option value="">Select make</option>
                {makeOptions.map(make => (
                  <option key={make} value={make}>
                    {make}
                  </option>
                ))}
              </FilterSelect>
              <FilterSelect
                value={modelInput}
                onChange={event => setModelInput(event.target.value)}
                disabled={!makeInput}
              >
                <option value="">Select model</option>
                {modelOptions.map(model => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </FilterSelect>
              <FilterButton type="button" onClick={handleAddMakeModel}>
                Add
              </FilterButton>
            </FilterRow>
            {draftFilters.makeModels?.length ? (
              <FilterList>
                {draftFilters.makeModels.map((pair, index) => (
                  <div key={`${pair.make}-${pair.model}-${index}`}>
                    {pair.make} {pair.model}{' '}
                    <FilterLinkButton
                      type="button"
                      onClick={() => handleRemoveMakeModel(index)}
                    >
                      remove
                    </FilterLinkButton>
                  </div>
                ))}
              </FilterList>
            ) : null}
          </FilterSection> */}

              <FilterSection>
                <FilterLabel>All years</FilterLabel>
                <FilterHelperText>
                  {(draftFilters.minYear ?? yearBounds.min).toString()} -{' '}
                  {(draftFilters.maxYear ?? yearBounds.max).toString()}
                </FilterHelperText>
                <RangeBar>
                  <RangeTrack />
                  <RangeSelection
                    style={{
                      left: `${getPercent(draftFilters.minYear ?? yearBounds.min, yearBounds.min, yearBounds.max)}%`,
                      right: `${100 - getPercent(draftFilters.maxYear ?? yearBounds.max, yearBounds.min, yearBounds.max)}%`,
                    }}
                  />
                  <FilterRange
                    type="range"
                    min={yearBounds.min}
                    max={yearBounds.max}
                    step={1}
                    value={draftFilters.minYear ?? yearBounds.min}
                    onChange={event =>
                      setDraftFilters(prev => {
                        const minYear = Number(event.target.value);
                        const maxYear = prev.maxYear ?? yearBounds.max;
                        return {
                          ...prev,
                          minYear,
                          maxYear: Math.max(maxYear, minYear),
                        };
                      })
                    }
                  />
                  <FilterRange
                    type="range"
                    min={yearBounds.min}
                    max={yearBounds.max}
                    step={1}
                    value={draftFilters.maxYear ?? yearBounds.max}
                    onChange={event =>
                      setDraftFilters(prev => {
                        const maxYear = Number(event.target.value);
                        const minYear = prev.minYear ?? yearBounds.min;
                        return {
                          ...prev,
                          minYear: Math.min(minYear, maxYear),
                          maxYear,
                        };
                      })
                    }
                  />
                </RangeBar>
              </FilterSection>

              <FilterLabel>Transmission</FilterLabel>
              <FilterSelect
                value={draftFilters.transmission ?? ''}
                onChange={event =>
                  setDraftFilters(prev => ({
                    ...prev,
                    transmission: event.target.value || undefined,
                  }))
                }
              >
                <option value="">Select</option>
                {TRANSMISSION_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FilterSelect>
            </FilterSection>
          </FilterModalContent>
          <FilterModalFooter>
            <FilterRow>
              <FilterButton type="button" onClick={() => setIsFilterModalOpen(false)}>
                Cancel
              </FilterButton>
              <FilterButton type="button" onClick={resetFilters}>
                Reset
              </FilterButton>
              <FilterButton type="button" onClick={applyFilters}>
                Apply filters
              </FilterButton>
            </FilterRow>
          </FilterModalFooter>
        </FilterModalLayout>
      </Modal>

      {/* Toggle button - only visible on screens smaller than medium */}
      <ViewToggleButton onClick={toggleView} $raised={viewMode === 'map' && Boolean(selectedCarId)}>
        <FontAwesomeIcon icon={viewMode === 'list' ? faMap : faList} />
        <span>{viewMode === 'list' ? 'Map' : 'List'}</span>
      </ViewToggleButton>
    </PageContainer>
  );
}
